import Decimal from "decimal.js";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  AmountCurrencyResponse,
  CurrencyPrimitive,
  FeeSettingsResponse,
} from "@/api/cashier";
import { getDecimalSeparator, moneyToMajorUnits } from "@/utils/helpers";

export type FormatAmountCurrency =
  | Pick<CurrencyPrimitive, "code" | "decimals">
  | Pick<AmountCurrencyResponse, "code" | "decimals">
  | undefined;

// Precision floor for exchange rates. Rates >= 0.1 keep their currency's own
// decimals, so this only matters as a lower bound for the trailing portion.
const MIN_RATE_FRACTION_DIGITS = 8;
// Upper bound to stay within Intl.NumberFormat's fraction-digit limit.
const MAX_RATE_FRACTION_DIGITS = 20;

/**
 * Counts the zeros between the decimal point and the first significant digit of
 * a positive sub-1 number (e.g. 0.0000168 -> 4). Returns 0 for non-finite
 * values or values outside the open interval (0, 1). Derived from the
 * normalized exponent so powers of ten (0.001 -> 1e-3) stay exact, unlike
 * Math.log10 which is prone to floating-point boundary errors.
 */
const countLeadingFractionZeros = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0 || value >= 1) return 0;
  const exponent = Number(value.toExponential().split("e")[1]);
  if (!Number.isFinite(exponent)) return 0;
  return Math.max(0, -exponent - 1);
};

export const useFormatAmount = () => {
  const { i18n } = useTranslation();

  return useMemo(() => {
    const formatAmount = (
      amount: number | string | Decimal | undefined | null,
      currency: FormatAmountCurrency,
      locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
    ) => {
      if (amount === null || amount === undefined || !currency) return "";

      const numericAmount =
        amount instanceof Decimal ? amount.toNumber() : Number(amount);

      const currencyCode =
        "currency_code" in currency
          ? currency.code
          : "code" in currency
            ? currency.code
            : "";
      if (!currencyCode) return "";
      try {
        return numericAmount.toLocaleString(locale, {
          style: "currency",
          currency: currencyCode,
          currencyDisplay: "symbol",
          minimumFractionDigits: 0,
          maximumFractionDigits: currency.decimals,
          useGrouping: true,
        });
      } catch {
        return `${numericAmount.toLocaleString(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: currency.decimals,
          useGrouping: true,
        })} ${currencyCode}`;
      }
    };

    const getValueFromBankingAmount = (money: AmountCurrencyResponse) => {
      return moneyToMajorUnits(money);
    };

    const formatBankingAmount = (
      amount: AmountCurrencyResponse,
      locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
    ) => {
      return formatAmount(getValueFromBankingAmount(amount), amount, locale);
    };

    const formatNumber = (
      amount: number | string | Decimal | undefined | null,
      currency: FormatAmountCurrency,
      locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
    ) => {
      if (amount === null || amount === undefined || !currency) return "";
      const numericAmount =
        amount instanceof Decimal ? amount.toNumber() : Number(amount);
      return numericAmount.toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: currency.decimals,
        useGrouping: true,
      });
    };

    const formatAmountRange = (
      min: number | string | undefined,
      max: number | string | undefined,
      currency: FormatAmountCurrency,
      locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
    ) => {
      if (typeof min === "undefined" || typeof max === "undefined" || !currency)
        return "";
      const code = "code" in currency ? currency.code : "";
      if (!code) return "";
      return `${Number(min).toLocaleString(locale, {
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: currency.decimals,
        useGrouping: true,
      })} - ${Number(max).toLocaleString(locale, {
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: currency.decimals,
        useGrouping: true,
      })} ${code}`;
    };

    const formatExchangeRate = (
      unit: number,
      currency: FormatAmountCurrency,
      exchange_rate: number,
      exchangeCurrency: FormatAmountCurrency,
      locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
    ) => {
      return {
        unit: formatAmount(unit, currency, locale),
        rate: formatRate(exchange_rate, exchangeCurrency, locale),
      };
    };

    const formatRate = (
      rate: number | string | undefined | null,
      currency: FormatAmountCurrency,
      locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
    ): { significant: string; trailing: string } => {
      if (rate === null || rate === undefined || !currency) {
        return { significant: "", trailing: "" };
      }

      const currencyCode =
        "currency_code" in currency
          ? currency.code
          : "code" in currency
            ? currency.code
            : "";

      if (!currencyCode) return { significant: "", trailing: "" };

      // Show `currency.decimals` worth of *significant* digits rather than a
      // fixed number of decimal places, so tiny rates (e.g. BTC per USD) keep
      // enough precision to reconcile with the displayed amounts instead of
      // collapsing to a few significant figures. Rates >= 0.1 have no leading
      // fraction zeros, so they are unaffected.
      const leadingZeros = countLeadingFractionZeros(Number(rate));
      const significantDecimals = Math.min(
        MAX_RATE_FRACTION_DIGITS,
        currency.decimals + leadingZeros
      );
      const totalDecimals = Math.min(
        MAX_RATE_FRACTION_DIGITS,
        Math.max(MIN_RATE_FRACTION_DIGITS, significantDecimals)
      );

      let parts: Intl.NumberFormatPart[];
      try {
        parts = new Intl.NumberFormat(locale, {
          style: "currency",
          currency: currencyCode,
          currencyDisplay: "symbol",
          minimumFractionDigits: totalDecimals,
          maximumFractionDigits: totalDecimals,
          useGrouping: true,
        }).formatToParts(Number(rate));
      } catch {
        parts = [
          { type: "currency", value: currencyCode },
          { type: "literal", value: "\u00a0" },
          ...new Intl.NumberFormat(locale, {
            style: "decimal",
            minimumFractionDigits: totalDecimals,
            maximumFractionDigits: totalDecimals,
            useGrouping: true,
          }).formatToParts(Number(rate)),
        ];
      }

      // Extract the fraction part and trim trailing zeros
      const fractionPart =
        parts.find((p) => p.type === "fraction")?.value ?? "";
      const trimmedFraction = fractionPart.replace(/0+$/, "");

      const significantFraction = fractionPart.slice(0, significantDecimals);
      const trailingFraction = trimmedFraction.slice(significantDecimals);

      // Reassemble parts, replacing fraction with significantFraction.
      // The decimal separator only gets dropped when there is neither a
      // significant nor a trailing fraction to render. For 0-decimal currencies
      // (e.g. JPY) every fractional digit is "trailing", so without this guard
      // the decimal point would be stripped and `¥97.71` would render as `¥9771`.
      const significantParts = parts
        .map((p) => {
          if (p.type === "fraction") return significantFraction;
          if (p.type === "decimal") {
            return significantFraction === "" && trailingFraction === ""
              ? ""
              : p.value;
          }
          return p.value;
        })
        .join("");

      return {
        significant: significantParts,
        trailing: trailingFraction,
      };
    };

    const formatFee = (
      fee: FeeSettingsResponse, // Supports percentage fees represented as a plain number.
      currency: FormatAmountCurrency,
      locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
    ) => {
      if (fee.type === "percentage") {
        return `${fee.value.amount}%`;
      }
      return formatAmount(
        getValueFromBankingAmount(fee.value),
        currency,
        locale
      );
    };

    const formatInput = (
      amount: Decimal | number | string | undefined,
      currency: FormatAmountCurrency,
      locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
    ) => {
      if (typeof amount === "undefined" || !currency) return "";
      const currencyCode = "code" in currency ? currency.code : "";
      if (!currencyCode) return "";
      const minFractionDigits = `${amount}`.split(".")[1]?.length ?? 0;
      return Number(amount).toLocaleString(locale, {
        currency: currencyCode,
        minimumFractionDigits: Math.min(minFractionDigits, currency.decimals),
        maximumFractionDigits: currency.decimals,
        useGrouping: true,
      });
    };

    const formatSanitizedInput = (
      numeric: string | Decimal,
      sanitized: string,
      currency: FormatAmountCurrency,
      locale: Intl.UnicodeBCP47LocaleIdentifier = i18n.language
    ) => {
      const decimalSep = getDecimalSeparator(locale);
      return (
        formatInput(numeric, currency, locale) +
        (sanitized.endsWith(".") ? decimalSep : "")
      );
    };

    return {
      getValueFromBankingAmount,
      formatAmount,
      formatNumber,
      formatBankingAmount,
      formatAmountRange,
      formatExchangeRate,
      formatFee,
      formatInput,
      formatSanitizedInput,
    };
  }, [i18n.language]);
};
